/**
 * The legal documents a user is asked to accept, and where the published copies
 * live.
 *
 * The app also bundles the full text (`busLineApp/assets/legalContent.ts`) and
 * shows it in a modal from Settings. The registration form does not use that: it
 * lives here in the shared library, and pulling an app-specific asset into a
 * library component would couple the two. So signup links to the PUBLISHED pages
 * instead — which is the better record anyway, since those are the documents the
 * consent row actually refers to.
 */
/**
 * The one address the legal documents name — for privacy questions, data-rights
 * requests, account closure and account security alike.
 *
 * It is a constant because it has been wrong twice. First it was
 * `privacy@buslinerecorder.com` and `support@buslinerecorder.com` on a domain that had never
 * been registered, so four clauses of a published privacy policy described a
 * right nobody could exercise. Then, with the legal copy fixed, the website's
 * footer and contact button went on offering `info@cwebspace.com` from their own
 * hardcoded copies — a site publishing one mailbox in its documents and a
 * different one in its footer has told the reader that one of the two is unread.
 *
 * `bin/CheckLegalVersions.sh` checks that whatever this resolves to sits on a
 * domain with an MX record, because an address that cannot receive mail is worse
 * than naming none: it looks like a route that exists.
 */
export const PRIVACY_CONTACT_EMAIL = 'info@buslinerecorder.com';

export const LEGAL_DOC_TYPES = ['privacy', 'terms', 'dpa'] as const;

export type LegalDocType = (typeof LEGAL_DOC_TYPES)[number];

/**
 * The documents EVERY account owes.
 *
 * 'dpa' is not one of them. It is the Art. 28 processing agreement — a contract
 * between us and a customer COMPANY — and the only person who can conclude it is
 * whoever holds that company's account. Showing it to a driver would be asking
 * them to sign something they have no authority over. Anything iterating "the
 * documents this user has to deal with" wants THIS list, not LEGAL_DOC_TYPES;
 * the longer one exists for validation and for looking a document up by type.
 */
export const USER_DOC_TYPES = ['privacy', 'terms'] as const;

export type UserDocType = (typeof USER_DOC_TYPES)[number];

/** The documents a COMPANY owes, accepted once per company by its owner. */
export const COMPANY_DOC_TYPES = ['dpa'] as const;

export type CompanyDocType = (typeof COMPANY_DOC_TYPES)[number];

/**
 * busLineWeb serves these two routes. The host is a literal rather than derived
 * from the API base URL because they are on different hosts — the API is at
 * `api.buslinerecorder.com` and the site is at `buslinerecorder.com` — and the
 * site has only one deployment, so there is no dev/prod variant to pick.
 *
 * These two URLs are what a signup checkbox links to, which makes them the
 * documents an account is recorded as having accepted. So they must point at the
 * site that is actually live: an installed build linking to a host that has been
 * retired shows a consent link that 404s, and the consent row it wrote still
 * claims the person read the document.
 */
export const LEGAL_DOC_URLS: Readonly<Record<LegalDocType, string>> = {
    privacy: 'https://buslinerecorder.com/privacy',
    terms: 'https://buslinerecorder.com/terms',
    dpa: 'https://buslinerecorder.com/dpa',
};

/**
 * Where the current sub-processor list is published.
 *
 * Separate from the DPA itself and deliberately so: Annex 2 names the providers,
 * but the agreement promises 30 days' notice of a change to them, and a promise
 * of notice is only keepable if the list has a stable address of its own that a
 * customer can check without re-reading a contract. It is also the page the
 * notification email links to.
 */
export const SUBPROCESSORS_URL = 'https://buslinerecorder.com/subprocessors';

/** One acceptance as GET /user/consents returns it. */
export interface UserConsent {
    ConsentID: number;
    DocType: LegalDocType | string;
    /**
     * The company a 'dpa' row was accepted for; 0 on a privacy or terms row.
     *
     * 0 rather than null so the server's unique key keeps collapsing repeat
     * acceptances — see the column comment in UserConsents. Treat it as "not
     * about a company", never as "company unknown".
     */
    CompanyID: number;
    DocVersion: string;
    AcceptedAt: string | null;
    Source: string;
    AppName: string;
    Locale: string;
}

/**
 * GET /user/consents.
 *
 * `required` is the SERVER's idea of the current version, not this build's.
 * `outstanding` is the server's own answer to "what is missing", so a client
 * never has to reimplement the comparison — see the note in the API's
 * `common/legal/legalVersions.ts` for why the server decides.
 */
export interface UserConsentsResponse {
    required: Record<LegalDocType, string>;
    consents: UserConsent[];
    /**
     * Account-level documents still owed — privacy and terms only.
     *
     * The DPA is NOT here and cannot be: the server does not know which company
     * the caller is asking about, and one owner may hold several. Ask
     * `outstandingCompanyConsents` with the company you mean.
     */
    outstanding: UserDocType[];
}

/**
 * Which company-level documents this company still owes.
 *
 * Client-side rather than served, because the server would have to guess which
 * company the question is about — the token carries a companyID, but a driver
 * has one too, and a driver owes nothing here. The caller knows which company it
 * owns; it passes that.
 *
 * `required` is the server's map from GET /user/consents, not this build's idea
 * of the current version: an installed app can be months behind, and a stale
 * client deciding a contract is current is exactly the failure this avoids.
 */
export function outstandingCompanyConsents(
    consents: readonly UserConsent[],
    companyID: number,
    required: Record<LegalDocType, string>,
): CompanyDocType[] {
    return COMPANY_DOC_TYPES.filter((docType) => {
        const latest = consents
            .filter((c) => c.DocType === docType && Number(c.CompanyID) === companyID)
            .reduce<UserConsent | null>(
                (best, c) => (best === null || c.ConsentID > best.ConsentID ? c : best),
                null,
            );
        return latest === null || latest.DocVersion !== required[docType];
    });
}
